<template>
	<ion-modal
		:is-open="isOpen"
		:backdrop-dismiss="backdropDismiss"
		v-bind="modalProps"
		@ion-modal-will-dismiss="$emit('will-dismiss', $event)"
		@ion-modal-did-dismiss="handleDidDismiss"
		@ion-modal-will-present="$emit('will-present', $event)"
		@ion-modal-did-present="$emit('did-present', $event)"
	>
		<!-- Header -->
		<ion-header>
			<ion-toolbar :color="headerColor">
				<slot name="header" :close="close" :open="open">
					<ion-title>{{ title }}</ion-title>
					<ion-buttons v-if="showCloseButton" slot="end">
						<ion-button @click="close">
							<ion-icon :icon="closeOutline" />
						</ion-button>
					</ion-buttons>
				</slot>
			</ion-toolbar>
		</ion-header>

		<!-- Main content -->
		<ion-content>
			<slot :close="close" :open="open" />
		</ion-content>

		<!-- Footer -->
		<ion-footer v-if="$slots.footer">
			<ion-toolbar>
				<slot name="footer" :close="close" :open="open" />
			</ion-toolbar>
		</ion-footer>
	</ion-modal>
</template>

<script setup lang="ts">
	import {
		IonModal,
		IonHeader,
		IonToolbar,
		IonTitle,
		IonButtons,
		IonButton,
		IonIcon,
		IonContent,
		IonFooter,
	} from "@ionic/vue";
	import { closeOutline } from "ionicons/icons";

	interface SgModalProps {
		// Core modal control
		title?: string;

		// Modal behavior
		backdropDismiss?: boolean;

		// Header customization
		showCloseButton?: boolean;
		headerColor?: string;

		// Ion-modal passthrough
		modalProps?: Record<string, unknown>;
	}

	withDefaults(defineProps<SgModalProps>(), {
		title: "",
		backdropDismiss: true,
		showCloseButton: true,
		headerColor: "branding",
		modalProps: () => ({}),
	});

	const isOpen = defineModel<boolean>({ default: false });

	const emit = defineEmits<{
		"will-dismiss": [event: CustomEvent];
		"did-dismiss": [event: CustomEvent];
		"will-present": [event: CustomEvent];
		"did-present": [event: CustomEvent];
	}>();

	// Modal control methods
	const open = () => {
		isOpen.value = true;
	};

	const close = () => {
		isOpen.value = false;
	};

	// Event handlers
	const handleDidDismiss = (event: CustomEvent) => {
		isOpen.value = false;
		emit("did-dismiss", event);
	};

	// Expose methods for ref-based control
	defineExpose({
		open,
		close,
	});
</script>
